'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface DocumentUploadProps {
  onDataChange: (data: DocumentUploadData) => void;
  initialData?: DocumentUploadData;
}

export interface DocumentUploadData {
  businessLicense: File | null;
  insurance: File | null;
  certifications: File[];
  ownershipProof: File | null;
}

interface UploadedFile {
  name: string;
  size: number;
  type: string;
}

const DocumentUpload = ({ onDataChange, initialData }: DocumentUploadProps) => {
  const [uploads, setUploads] = useState<{
    businessLicense: UploadedFile | null;
    insurance: UploadedFile | null;
    certifications: UploadedFile[];
    ownershipProof: UploadedFile | null;
  }>({
    businessLicense: null,
    insurance: null,
    certifications: [],
    ownershipProof: null
  });

  const [dragOver, setDragOver] = useState<string | null>(null);

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const handleFileSelect = (field: keyof DocumentUploadData, file: File) => {
    const fileInfo: UploadedFile = {
      name: file.name,
      size: file.size,
      type: file.type
    };

    if (field === 'certifications') {
      const updatedCerts = [...uploads.certifications, fileInfo];
      setUploads(prev => ({ ...prev, certifications: updatedCerts }));
    } else {
      setUploads(prev => ({ ...prev, [field]: fileInfo }));
    }

    // Notify parent component
    const updatedData: DocumentUploadData = {
      businessLicense: field === 'businessLicense' ? file : null,
      insurance: field === 'insurance' ? file : null,
      certifications: field === 'certifications' ? [file] : [],
      ownershipProof: field === 'ownershipProof' ? file : null
    };
    onDataChange(updatedData);
  };

  const handleRemoveFile = (field: keyof DocumentUploadData, index?: number) => {
    if (field === 'certifications' && index !== undefined) {
      const updatedCerts = uploads.certifications.filter((_, i) => i !== index);
      setUploads(prev => ({ ...prev, certifications: updatedCerts }));
    } else {
      setUploads(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleDragOver = (e: React.DragEvent, field: string) => {
    e.preventDefault();
    setDragOver(field);
  };

  const handleDragLeave = () => {
    setDragOver(null);
  };

  const handleDrop = (e: React.DragEvent, field: keyof DocumentUploadData) => {
    e.preventDefault();
    setDragOver(null);
    
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(field, file);
    }
  };

  const documentTypes = [
    {
      field: 'businessLicense' as keyof DocumentUploadData,
      title: 'Business License',
      description: 'Valid business license or registration',
      required: true,
      acceptedFormats: '.pdf, .jpg, .png',
      icon: 'DocumentTextIcon'
    },
    {
      field: 'insurance' as keyof DocumentUploadData,
      title: 'Insurance Certificate',
      description: 'Proof of liability insurance',
      required: true,
      acceptedFormats: '.pdf, .jpg, .png',
      icon: 'ShieldCheckIcon'
    },
    {
      field: 'certifications' as keyof DocumentUploadData,
      title: 'Certifications',
      description: 'ASE, manufacturer, or other certifications',
      required: false,
      acceptedFormats: '.pdf, .jpg, .png',
      icon: 'AcademicCapIcon',
      multiple: true
    },
    {
      field: 'ownershipProof' as keyof DocumentUploadData,
      title: 'Ownership Verification',
      description: 'Utility bill, lease agreement, or property deed',
      required: true,
      acceptedFormats: '.pdf, .jpg, .png',
      icon: 'BuildingOfficeIcon'
    }
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-start space-x-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 flex-shrink-0">
          <Icon name="DocumentArrowUpIcon" size={20} className="text-primary" />
        </div>
        <div className="flex-1">
          <h3 className="font-heading text-lg font-semibold text-text-primary mb-1">
            Document Verification
          </h3>
          <p className="font-body text-sm text-text-secondary">
            Upload required documents to verify your business ownership
          </p>
        </div>
      </div>

      <div className="rounded-lg bg-muted p-4">
        <div className="flex items-start space-x-3">
          <Icon name="InformationCircleIcon" size={20} className="text-primary flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-caption text-xs font-medium text-text-primary mb-1">
              Document Requirements
            </p>
            <ul className="font-body text-xs text-text-secondary space-y-1">
              <li>• All documents must be clear and legible</li>
              <li>• Accepted formats: PDF, JPG, PNG (max 10MB per file)</li>
              <li>• Documents must be current and valid</li>
              <li>• Business name must match across all documents</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {documentTypes.map((doc) => (
          <div key={doc.field} className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="font-caption text-xs font-medium text-text-secondary uppercase tracking-wider">
                {doc.title} {doc.required && <span className="text-error">*</span>}
              </label>
              <span className="font-caption text-xs text-muted-foreground">
                {doc.acceptedFormats}
              </span>
            </div>

            {doc.multiple && uploads.certifications.length > 0 ? (
              <div className="space-y-2">
                {uploads.certifications.map((file, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between rounded-lg border border-border bg-card px-4 py-3"
                  >
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      <Icon name="DocumentIcon" size={20} className="text-primary flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="font-body text-sm font-medium text-text-primary truncate">
                          {file.name}
                        </p>
                        <p className="font-caption text-xs text-muted-foreground">
                          {formatFileSize(file.size)}
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveFile('certifications', index)}
                      className="flex items-center justify-center rounded-md p-2 text-text-secondary transition-smooth hover:bg-muted hover:text-error"
                      aria-label="Remove file"
                    >
                      <Icon name="XMarkIcon" size={18} />
                    </button>
                  </div>
                ))}
              </div>
            ) : uploads[doc.field as keyof typeof uploads] && !doc.multiple ? (
              <div className="flex items-center justify-between rounded-lg border border-border bg-card px-4 py-3">
                <div className="flex items-center space-x-3 flex-1 min-w-0">
                  <Icon name="DocumentIcon" size={20} className="text-primary flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-body text-sm font-medium text-text-primary truncate">
                      {(uploads[doc.field as keyof typeof uploads] as UploadedFile)?.name}
                    </p>
                    <p className="font-caption text-xs text-muted-foreground">
                      {formatFileSize((uploads[doc.field as keyof typeof uploads] as UploadedFile)?.size || 0)}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleRemoveFile(doc.field)}
                  className="flex items-center justify-center rounded-md p-2 text-text-secondary transition-smooth hover:bg-muted hover:text-error"
                  aria-label="Remove file"
                >
                  <Icon name="XMarkIcon" size={18} />
                </button>
              </div>
            ) : (
              <div
                onDragOver={(e) => handleDragOver(e, doc.field)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, doc.field)}
                className={`relative rounded-lg border-2 border-dashed transition-smooth ${
                  dragOver === doc.field
                    ? 'border-primary bg-primary/5' :'border-border bg-card hover:border-primary/50 hover:bg-muted'
                }`}
              >
                <input
                  type="file"
                  id={doc.field}
                  accept={doc.acceptedFormats}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleFileSelect(doc.field, file);
                  }}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
                <div className="flex flex-col items-center justify-center px-6 py-8 text-center">
                  <Icon name={doc.icon as any} size={32} className="text-muted-foreground mb-3" />
                  <p className="font-body text-sm font-medium text-text-primary mb-1">
                    {doc.description}
                  </p>
                  <p className="font-caption text-xs text-muted-foreground mb-3">
                    Drag and drop or click to browse
                  </p>
                  <button
                    type="button"
                    className="inline-flex items-center space-x-2 rounded-lg border border-primary px-4 py-2 font-body text-sm font-medium text-primary transition-smooth hover:bg-primary hover:text-primary-foreground"
                  >
                    <Icon name="ArrowUpTrayIcon" size={16} />
                    <span>Choose File</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default DocumentUpload;